// Opening a .docx package, with plain messages for files that will not open.
//
// JSZip's own errors ("Can't find end of central directory : is this a zip file ? If it is,
// see https://stuk.github.io/jszip/…") must never reach a user: the app shows the message after
// 'Could not read the original document ("name").', as the CLI does.
import JSZip from 'jszip';

export const UNREADABLE_DOCX = "This file isn't a readable Word document. It may be damaged, or it may not really be a .docx.";
export const PASSWORD_PROTECTED = 'This document is password-protected. Remove the password in Word (File > Info > Protect Document), save it, and try again.';

const OLE_MAGIC = [0xD0, 0xCF, 0x11, 0xE0, 0xA1, 0xB1, 0x1A, 0xE1];

/** An OLE compound file: a legacy .doc, or a password-protected Office file. */
export function isOleFile(bytes: Uint8Array): boolean {
  return bytes.length >= 8 && OLE_MAGIC.every((b, i) => bytes[i] === b);
}

/** A password-protected .docx/.xlsx is an OLE container holding "EncryptedPackage" (ECMA-376
 *  Part 2 / MS-OFFCRYPTO), not a zip. Its directory names are UTF-16LE. */
export function isEncryptedOoxml(bytes: Uint8Array): boolean {
  if (!isOleFile(bytes)) return false;
  const name = 'EncryptedPackage';
  outer: for (let i = 0; i + name.length * 2 <= bytes.length; i++) {
    for (let k = 0; k < name.length; k++) {
      if (bytes[i + 2 * k] !== name.charCodeAt(k) || bytes[i + 2 * k + 1] !== 0) continue outer;
    }
    return true;
  }
  return false;
}

/** Load a .docx as a zip holding word/document.xml, or throw one of the plain messages above. */
export async function openDocxZip(data: ArrayBuffer | Uint8Array): Promise<any> {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  if (isEncryptedOoxml(bytes)) throw new Error(PASSWORD_PROTECTED);
  let zip: any;
  try { zip = await JSZip.loadAsync(bytes); } catch { throw new Error(UNREADABLE_DOCX); }
  if (!zip.file('word/document.xml')) throw new Error(UNREADABLE_DOCX);
  return zip;
}
